import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { supabase } from '@/lib/supabase';
import { mockShopProducts, type ShopProduct } from '@/mocks/shop';
import { useCart } from '@/context/CartContext';

const shopHero =
  'https://readdy.ai/api/search-image?query=Modern%20African%20ecommerce%20marketplace%20concept%20with%20vibrant%20colorful%20product%20displays%20floating%20in%20a%20clean%20digital%20shopping%20interface%2C%20warm%20lighting%2C%20stylized%20abstract%20composition%20with%20rich%20orange%20and%20teal%20tones%2C%20professional%20technology%20illustration%2C%20high%20detail&width=1600&height=800&seq=shop-hero-market-01&orientation=landscape';

interface ListingDbRow {
  id: string;
  type: string;
  title: string;
  description: string | null;
  price: number | null;
  category_id: string | null;
  city: string | null;
  images: string[] | null;
  contact_name: string | null;
  contact_phone: string | null;
  created_at: string | null;
}

const SHOP_CATEGORY = 'Boutique';
type SortKey = 'recent' | 'price-asc' | 'price-desc';

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

function similarityScore(product: ShopProduct, queryTokens: string[]): number {
  const titleTokens = tokenize(product.title);
  const descTokens = tokenize(product.description);
  let score = 0;
  for (const q of queryTokens) {
    if (titleTokens.some((t) => t.includes(q) || q.includes(t))) score += 3;
    if (descTokens.some((t) => t.includes(q) || q.includes(t))) score += 1;
    if (product.category.toLowerCase().includes(q)) score += 2;
    if (product.city.toLowerCase().includes(q)) score += 1;
  }
  return score;
}

function findSimilarProducts(
  allProducts: ShopProduct[],
  query: string,
  excludeIds: string[],
  limit = 8,
): ShopProduct[] {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return [];
  const scored = allProducts
    .filter((p) => !excludeIds.includes(p.id))
    .map((p) => ({ product: p, score: similarityScore(p, queryTokens) }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.product);
  return scored;
}

function ShopPage() {
  const [products, setProducts] = useState<ShopProduct[]>(mockShopProducts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(SHOP_CATEGORY);
  const [categories, setCategories] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('recent');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const { addItem, openCart } = useCart();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: catData, error: catErr } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');
      if (catErr) throw catErr;

      const catMap: Record<string, string> = {};
      const catNames: string[] = [];
      (catData as { id: string; name: string }[]).forEach((c) => {
        catMap[c.id] = c.name;
        if (!catNames.includes(c.name)) catNames.push(c.name);
      });

      const { data, error: listErr } = await supabase
        .from('listings')
        .select('*')
        .eq('type', 'produit')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (listErr) throw listErr;

      if (data && data.length > 0) {
        const mapped: ShopProduct[] = (data as ListingDbRow[]).map((row) => ({
          id: row.id,
          title: row.title,
          description: row.description || '',
          price: Number(row.price) || 0,
          category: row.category_id ? catMap[row.category_id] || 'Boutique' : 'Boutique',
          city: row.city || 'Lomé',
          image: row.images && row.images.length > 0 ? row.images[0] : '',
          sellerName: row.contact_name || 'Vendeur',
          sellerPhone: row.contact_phone || '',
          postedAt: row.created_at ? row.created_at.slice(0, 10) : '',
          rating: 4.3,
          reviewsCount: Math.floor(Math.random() * 500) + 50,
        }));
        setProducts(mapped);
      }

      setCategories(catNames.length > 0 ? catNames : [SHOP_CATEGORY]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les produits.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchesCategory = !selectedCategory || p.category === selectedCategory;
      const q = debouncedSearch.toLowerCase();
      const matchesSearch =
        !q || p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
      const min = minPrice ? Number(minPrice) : 0;
      const max = maxPrice ? Number(maxPrice) : Infinity;
      const matchesPrice = p.price >= min && p.price <= max;
      return matchesCategory && matchesSearch && matchesPrice;
    });
  }, [products, selectedCategory, debouncedSearch, minPrice, maxPrice]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sort === 'price-asc') return a.price - b.price;
      if (sort === 'price-desc') return b.price - a.price;
      return b.postedAt.localeCompare(a.postedAt);
    });
  }, [filtered, sort]);

  const similarProducts = useMemo(() => {
    if (sorted.length > 0 || debouncedSearch.length < 2) return [];
    return findSimilarProducts(products, debouncedSearch, []);
  }, [sorted.length, debouncedSearch, products]);

  const hasActiveFilters = debouncedSearch || minPrice || maxPrice || selectedCategory;

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setMinPrice('');
    setMaxPrice('');
    setSelectedCategory(null);
  };

  const handleAddToCart = (product: ShopProduct) => {
    addItem({
      id: product.id,
      title: product.title,
      price: product.price,
      image: product.image,
      sellerName: product.sellerName,
    });
    openCart();
  };

  const renderProductCard = (product: ShopProduct) => {
    const fullStars = Math.floor(product.rating || 0);
    const hasHalf = (product.rating || 0) - fullStars >= 0.3;
    const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
    return (
      <article
        key={product.id}
        data-product-shop
        className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden flex flex-col hover:border-primary-400/40 transition-all duration-300"
      >
        <div className="relative">
          <Link
            to={`/shop/${product.id}`}
            className="aspect-square w-full overflow-hidden bg-background-200/60 block"
          >
            {product.image ? (
              <img
                src={product.image}
                alt={product.title}
                title={`${product.title} - WorldApeiron Shop`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <i className="ri-image-line text-3xl text-foreground-400" />
              </div>
            )}
          </Link>
          <button
            onClick={() => handleAddToCart(product)}
            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-50 hover:bg-primary-500 hover:text-foreground-950 transition-colors"
            aria-label="Ajouter au panier"
          >
            <i className="ri-shopping-cart-2-line text-base" />
          </button>
        </div>

        <div className="p-3 flex-1 flex flex-col">
          <Link to={`/shop/${product.id}`}>
            <h2 className="text-sm font-semibold text-foreground-50 leading-snug hover:text-primary-400 transition-colors line-clamp-2">
              {product.title}
            </h2>
          </Link>

          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-sm font-semibold text-accent-400">
              {product.rating?.toFixed(1).replace('.', ',')}
            </span>
            <div className="flex items-center">
              {Array.from({ length: fullStars }).map((_, i) => (
                <i key={`f-${i}`} className="ri-star-fill text-accent-400 text-xs" />
              ))}
              {hasHalf && <i className="ri-star-half-fill text-accent-400 text-xs" />}
              {Array.from({ length: emptyStars }).map((_, i) => (
                <i key={`e-${i}`} className="ri-star-line text-accent-400/50 text-xs" />
              ))}
            </div>
            <span className="text-xs text-foreground-400">
              {product.reviewsCount?.toLocaleString('fr-FR')}
            </span>
          </div>

          <div className="mt-2">
            <span className="font-heading font-bold text-base text-primary-400">
              {product.price.toLocaleString('fr-FR')} FCFA
            </span>
          </div>

          <div className="mt-3">
            <Link
              to={`/shop/${product.id}`}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-background-300/60 text-foreground-50 font-medium text-sm hover:bg-background-200/60 transition-colors whitespace-nowrap"
            >
              Voir les options
            </Link>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={shopHero}
              alt="WorldApeiron Shop - marketplace de produits"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">
                WorldApeiron Shop
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
                Achetez &amp; vendez dans la Boutique
              </h1>
              <p className="text-base md:text-lg text-foreground-400 max-w-xl mb-8 leading-relaxed">
                Découvrez les produits publiés par la communauté WorldApeiron. Publiez vos propres
                produits et touchez des acheteurs partout au Togo et en Afrique.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1 max-w-xl flex items-center bg-background-50 border border-background-300/60 rounded-full overflow-hidden focus-within:border-primary-400">
                  <div className="w-11 h-11 flex items-center justify-center shrink-0">
                    <i className="ri-search-line text-foreground-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un produit..."
                    className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 pr-4 outline-none"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="w-9 h-9 flex items-center justify-center shrink-0 text-foreground-400 hover:text-foreground-50"
                    >
                      <i className="ri-close-line" />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border font-semibold text-sm whitespace-nowrap transition-colors ${
                    showFilters || hasActiveFilters
                      ? 'bg-primary-500 text-foreground-950 border-primary-500 hover:bg-primary-400'
                      : 'border-background-300/60 text-foreground-50 hover:bg-background-200/60'
                  }`}
                >
                  <i className="ri-filter-3-line text-lg" />
                  Filtres
                </button>
                <Link
                  to="/dashboard/listings/new"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-full hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  Vendre un produit
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="shop" />
        </section>

        {/* Expandable Filters */}
        {showFilters && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 pt-4">
            <div className="bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-foreground-50">Affiner la recherche</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-400 hover:text-primary-300 font-medium"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                    Prix minimum (FCFA)
                  </label>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="Ex: 5000"
                    className="w-full bg-background-50 border border-background-300/60 rounded-lg px-3 py-2 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                    Prix maximum (FCFA)
                  </label>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="Ex: 50000"
                    className="w-full bg-background-50 border border-background-300/60 rounded-lg px-3 py-2 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                    Catégorie
                  </label>
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value || null)}
                    className="w-full bg-background-50 border border-background-300/60 rounded-lg px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
                  >
                    <option value="">Toutes les catégories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                    Trier par
                  </label>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortKey)}
                    className="w-full bg-background-50 border border-background-300/60 rounded-lg px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
                  >
                    <option value="recent">Plus récents</option>
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                  </select>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Category chips (compact, only when filters closed) */}
        {!showFilters && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-sm font-semibold text-foreground-400 mr-1">
                {filtered.length} produit{filtered.length > 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === null
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-primary-400/40'
                }`}
              >
                Tous
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === cat
                      ? 'bg-primary-500 text-foreground-950'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-primary-400/40'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Products grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : sorted.length === 0 ? (
            <>
              {/* No exact results — show similar products */}
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100">
                  <i className="ri-search-2-line text-2xl text-secondary-400" />
                </div>
                <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                  Aucun résultat exact pour « {debouncedSearch} »
                </h2>
                <p className="text-sm text-foreground-400 mb-2">
                  Essayez d'autres mots-clés ou affinez vos filtres.
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-400 hover:text-primary-300 font-medium mb-8"
                  >
                    Réinitialiser les filtres
                  </button>
                )}
              </div>

              {similarProducts.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-heading font-bold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                    <i className="ri-sparkling-2-line text-accent-400" />
                    Produits similaires à votre recherche
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {similarProducts.map(renderProductCard)}
                  </div>
                </div>
              )}

              {similarProducts.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-foreground-400 mb-6">
                    Aucun produit similaire trouvé non plus.
                  </p>
                  <Link
                    to="/dashboard/listings/new"
                    className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
                  >
                    Publier un produit
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {sorted.map(renderProductCard)}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100 border-t border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Vous avez un produit à vendre ?
            </h2>
            <p className="text-foreground-400 mb-8">
              Rejoignez la communauté et publiez vos produits dans la Boutique WorldApeiron en
              quelques secondes.
            </p>
            <Link
              to="/dashboard/listings/new"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-store-3-line text-lg" />
              Ouvrir ma boutique
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ShopPage;